/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 31-08-2026
 *
 * De chat bij een model (golf a1): de regels, of chatten hier mag, het versturen en het melden
 * van gelezen berichten. Dit is de eerste verbruiker van de nieuwe backendlaag — binnenkomende
 * berichten komen via de notify-stroom van `ApiService` binnen in plaats van via een
 * `$rootScope`-broadcast in AngularJS.
 *
 * De historie reist mee in het laden van het model (`OpslagService.bijGeladen`). Het `notifyId` — de
 * chat hangt altijd aan het hoofdmodel — komt sinds golf a3 uit diezelfde opslagketen.
 */
import {computed, inject, Injectable, signal} from '@angular/core';
import {Sihwlog} from 'sihw-ngx';
import {ApiService, type ModelId} from '../app/backend/api.service';
import {SessieService} from './sessie.service';
import {SamenwerkingService} from './samenwerking.service';
import {OpslagService} from '../app/model/opslag.service';

/**
 * Eén chatregel zoals het backend die aanlevert, zowel bij het laden van een model als in een
 * chat-notificatie.
 */
export interface ChatRegel {
    id: number;
    tekst: string;
    user: string;
    moment: number; //epoch in ms
    nieuw?: boolean;
}

/**
 * Een binnengekomen chat-notificatie: een chatregel met het model waar hij bij hoort.
 */
interface ChatNotify extends ChatRegel {
    model: ModelId;
}

@Injectable({providedIn: 'root'})
export class ChatService {

    private readonly log = new Sihwlog('info', 'ChatService');

    /**
     * De backendlaag: de chatcalls en de notify-stroom waarop binnenkomende berichten staan.
     */
    private readonly api = inject(ApiService);

    /**
     * De sessie: het project bepaalt of en wanneer er gechat mag worden.
     */
    private readonly sessie = inject(SessieService);

    /**
     * De samenwerk-sessie-state: in de stand `samenwerken` mag er alleen gechat worden als dit
     * model daadwerkelijk in een samenwerking zit, en zonder verbinding komt een bericht nergens aan.
     */
    private readonly samenwerking = inject(SamenwerkingService);

    /**
     * De opslagketen: daar woont het model waaronder de chat hangt.
     */
    private readonly opslag = inject(OpslagService);

    /**
     * De chatregels van het huidige model, oudste eerst.
     */
    private readonly _regels = signal<ChatRegel[]>([]);

    /**
     * Loopt op bij elke modelwissel; de weergave gebruikt dat om zichzelf te sluiten.
     */
    private readonly _sessieteller = signal(0);

    constructor() {
        this.api.onNotify<ChatNotify>('chat').subscribe(bericht => this.ontvang(bericht));
        //de historie reist mee met het geladen model, en bij een ander model begint de chat leeg;
        //sinds golf a3 luisteren we daarvoor op de opslagketen in plaats van dat ng1 ons aanstuurt
        this.opslag.bijGeladen(geladen => this.zetRegels(geladen.chat));
        this.opslag.bijGereset(() => this.reset());
    }

    /**
     * De chatregels van het huidige model.
     */
    readonly regels = this._regels.asReadonly();

    /**
     * Loopt op bij elke modelwissel.
     */
    readonly sessieteller = this._sessieteller.asReadonly();

    /**
     * Mag er bij dit model gechat worden? In de projectstand `samenwerken` alleen als het model
     * ook echt in een samenwerking zit.
     */
    readonly magChat = computed(() => {
        switch (this.sessie.chatmodus()) {
            case 'altijd':
                return true;
            case 'samenwerken':
                return this.samenwerking.werkSamen();
            default:
                return false;
        }
    });

    /**
     * Mag er nu een bericht de deur uit? Bovenop `magChat` telt de verbinding mee: zonder verbinding
     * komt een bericht nergens aan, en dan hoort het niet te lijken alsof het verstuurd is. Bewust
     * losgehouden van `magChat`, want de regels die er al staan mogen gewoon leesbaar blijven.
     *
     * De guard stond tot golf a5 op `fullReadonly`, en sloot daarmee ook een replay uit. Sinds a4
     * blijft de registratie tijdens een replay juist staan, zodat de chat doorloopt: wie terugbladert
     * kan zijn model niet muteren, maar wel meepraten.
     */
    readonly magVersturen = computed(() => this.magChat() && this.samenwerking.verbonden());

    /**
     * Zijn er berichten die de gebruiker nog niet gezien heeft?
     */
    readonly heeftNieuw = computed(() => this._regels().some(regel => regel.nieuw));

    /**
     * Zet de chathistorie zoals die met het geladen model meekwam.
     */
    zetRegels(regels: ChatRegel[] | null | undefined): void {
        this._regels.set([...(regels || [])]);
    }

    /**
     * Er is een ander model geladen: de chat begint leeg en de weergave gaat dicht.
     */
    reset(): void {
        this._regels.set([]);
        this._sessieteller.update(teller => teller + 1);
    }

    /**
     * Verstuurt een door de gebruiker ingetikt bericht. Zonder opgeslagen model is er geen
     * chat om aan te hangen.
     */
    verstuur(tekst: string): void {
        const notifyId = this.opslag.notifyId();
        if (!(notifyId && tekst?.length && this.magVersturen())) {
            return;
        }
        void this.api.chatSend(tekst, notifyId);
    }

    /**
     * Meldt de opgegeven berichten als gelezen: lokaal niet meer nieuw, en het backend weet het
     * ook.
     */
    meldGelezen(ids: number[]): void {
        if (!ids?.length) {
            return;
        }
        this._regels.update(regels =>
            regels.map(regel => (regel.nieuw && ids.includes(regel.id)) ? {...regel, nieuw: false} : regel));
        void this.api.chatGelezen(ids);
    }

    /**
     * Verwerkt een binnengekomen chatbericht: alleen als chatten mag en het bericht bij het
     * model hoort dat we volgen.
     */
    private ontvang(bericht: ChatNotify): void {
        const notifyId = this.opslag.notifyId();
        if (!(this.magChat() && bericht.model === notifyId)) {
            this.log.debug(`Chat mag niet. Data model ${bericht.model} notifyId ${notifyId}`);
            return;
        }
        this.log.debug('toevoegen aan chat', bericht);
        this._regels.update(regels => [...regels, bericht]);
    }
}
